<template>
  <v-card id="account-species-group-form">
    <v-card-title class="primary white--text">
      <v-row>
        <v-col>
          <span class="headline">
            {{ title }}
          </span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions hideRefresh hideFullscreen/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row style="width: calc(100% - 40px)">
        <v-col cols="12">
          <v-text-field
            id="species-group-label"
            ref="speciesGroupLabel"
            spellcheck="false"
            :maxLength="maxLabelLength"
            :rules="[rules.required,
            noDuplicateGroupLabelsFieldRule,
            validLabelLengthFieldRule]"
            :label="$t('label')"
            v-model="speciesGroupBuilder.label">
          </v-text-field>
        </v-col>

        <v-col cols="12">
          <LoadableComponent
            :loading="loading">
            <SpeciesAutocomplete
              :elementId="'species-selector'"
              :label="$t('species')"
              :multiple="true"
              :speciesList="speciesAvailableToSelect"
              :initSelection="initialSpeciesSelection"
              @species-chosen="onSpeciesChanged"/>
          </LoadableComponent>
        </v-col>

        <v-col cols="12">
          <LoadableComponent
          :loading="loading">
            <LengthGroupAutocomplete
            :elementId="lengthGroupAutocompleteId"
            :lengthGroups="accountLengthGroups"
            :initSelection="speciesGroupBuilder.lengthGroups"
            :multiple="true"
            @length-group-chosen="onLengthGroupsChanged"/>
          </LoadableComponent>
        </v-col>

        <v-col cols="12">
          <LoadableComponent
          :loading="loading">
            <DiameterGroupAutocomplete
            :elementId="diameterGroupAutocompleteId"
            :diameterGroups="accountDiameterGroups"
            :initSelection="speciesGroupBuilder.diameterGroups"
            :multiple="true"
            @diameter-group-chosen="onDiameterGroupsChanged"
            forbidOverlap/>
          </LoadableComponent>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="py-4 mx-0">
      <v-row align="center" dense>
        <v-col>
          <v-btn color="primary" outlined @click="handleCancel" tabindex="-1">
            <span>
              {{ $t('cancel') }}
            </span>
          </v-btn>
        </v-col>

        <v-col cols="auto" v-if="!speciesGroupValid">
          <Icon
          :tooltipText="invalidTooltip"
          :small="false"
          iconColor="error"
          icon="mdi-alert"/>
        </v-col>

        <v-col cols="auto">
          <v-btn color="primary" id="save" :disabled="!speciesGroupValid" @click="handleSave">
            <span>
              {{ $t('save') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import fieldRules from '@/utils/rules.js'
import { speciesSorter } from '@/components/account/account-scaling/species-groups/SpeciesGroups.js'
import { mapGetters, mapActions } from 'vuex/dist/vuex.common.js'
import { cloneDeep } from 'lodash'
import { lengthGroupSorter } from '@/components/account/account-scaling/length-groups/LengthGroups.js'
import { diameterGroupSorter } from '@/components/account/account-scaling/diameter-groups/DiameterGroups.js'

const MAX_LABEL_LENGTH = 32

export default {
  name: 'AccountSpeciesGroupForm',

  components: {
    LengthGroupAutocomplete: () => import('@/components/account/account-scaling/length-groups/LengthGroupAutocomplete.vue'),
    DiameterGroupAutocomplete: () => import('@/components/account/account-scaling/diameter-groups/DiameterGroupAutocomplete.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    SpeciesAutocomplete: () => import('@/components/settings-components/product-settings/species/SpeciesAutocomplete.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    account: {
      type: Object,
      required: true
    },

    speciesGroup: {
      type: Object,
      required: false,
      default: () => ({
        label: '',
        species: [],
        lengthGroups: [],
        diameterGroups: []
      })
    }
  },

  data: () => ({
    maxLabelLength: MAX_LABEL_LENGTH,
    rules: fieldRules.rules,
    speciesGroupBuilder: {
      label: '',
      species: [],
      lengthGroups: [],
      diameterGroups: []
    },
    loading: true,
    lengthGroupAutocompleteId: 'length-group-autocomplete',
    diameterGroupAutocompleteId: 'diameter-group-autocomplete',
    initialSpeciesSelection: null
  }),

  async created () {
    await this.fetchAccountLengthGroups(this.accountId)
    await this.fetchAccountDiameterGroups(this.accountId)
    if (this.speciesGroup) this.speciesGroupBuilder = cloneDeep(this.speciesGroup)
    this.initialSpeciesSelection = this.speciesGroupBuilder.species
    document.addEventListener('keyup', this.onKeyup)
    this.loading = false
  },

  beforeDestroy () {
    document.removeEventListener('keyup', this.onKeyup)
  },

  mounted () {
    this.$nextTick(() => {
      this.focus('speciesGroupLabel')
    })
  },

  computed: {
    ...mapGetters('account-scaling', ['accountLengthGroups', 'accountDiameterGroups',
      'accountSpeciesGroups']),
    ...mapGetters('species', ['allSpecies']),
    ...mapGetters('product', ['allProducts']),

    accountId () {
      return this.account.accountId
    },

    title () {
      return this.speciesGroupBuilder.speciesGroupId
        ? this.$t('editingSpeciesGroup')
        : this.$t('creatingSpeciesGroup')
    },

    speciesGroupValid () {
      return this.hasValidLabel &&
      this.hasSpeciesGroup &&
      this.noDuplicateGroupLabels &&
      this.noOverlappingDiameterGroups
    },

    hasValidLabel () {
      if (!this.speciesGroupBuilder?.label) return false
      const { label } = this.speciesGroupBuilder
      return label?.length <= MAX_LABEL_LENGTH
    },

    hasSpeciesGroup () {
      return this.speciesGroupBuilder.species.length > 0
    },

    noDuplicateGroupLabels () {
      return this.accountSpeciesGroups
        .every(speciesGroup => speciesGroup.label !== this.speciesGroupBuilder.label ||
          this.isBeingEdited(speciesGroup))
    },

    noOverlappingDiameterGroups () {
      return this.speciesGroupBuilder.diameterGroups.length < 2 ||
      this.speciesGroupBuilder.diameterGroups.every(dg => {
        const otherGroups = this.speciesGroupBuilder.diameterGroups.filter(other => other.diameterGroupId !== dg.diameterGroupId)
        return otherGroups.every(other => dg.lowerBound > other.upperBound || dg.upperBound < other.lowerBound)
      })
    },

    invalidTooltip () {
      return !this.noOverlappingDiameterGroups
        ? this.$t('overlappingDiameterGroups')
        : !this.noDuplicateGroupLabels
          ? this.$t('cannotDuplicateLabel')
          : this.$t('speciesGroupNotValid')
    },

    speciesAvailableToSelect () {
      const accountProductRows = this.account.accountProducts ?? []
      return this.allSpecies.filter(s => {
        const isOnAccountProduct = accountProductRows.some(p => {
          const product = this.allProducts.find(ap => ap.productId === p.productId)
          return product?.isScalable && product?.productSpecies.some(ps => ps.speciesId === s.speciesId)
        })
        const isOnFallbackProduct = this.allProducts.some(p => p.productId === this.account.fallbackProductId &&
          p.productSpecies.some(ps => ps.speciesId === s.speciesId))
        return (isOnAccountProduct || isOnFallbackProduct)
      })
    }
  },

  methods: {
    ...mapActions('account-scaling', ['fetchAccountLengthGroups', 'fetchAccountDiameterGroups',
      'fetchAccountSpeciesGroups']),

    focus (refString) {
      this.$nextTick(_ => {
        this.$refs[refString].focus()
      })
    },

    validLabelLengthFieldRule () {
      return this.hasValidLabel ||
        this.$t('cannotExceedNCharacters', { n: MAX_LABEL_LENGTH })
    },

    noDuplicateGroupLabelsFieldRule () {
      return this.noDuplicateGroupLabels ||
        this.$t('cannotDuplicateLabel')
    },

    handleSave () {
      if (this.speciesGroupValid) {
        this.$emit('save', this.speciesGroupBuilder)
      } else {
        this.setSnackError(this.$t('speciesGroupNotValid'))
      }
    },

    handleCancel () {
      this.$emit('cancel')
    },

    onKeyup (e) {
      if (this.inputIsActive()) {
        return
      }
      if (e.key.toLowerCase() === 'enter' && e.target?.id !== 'save') {
        this.handleSave()
      }
    },

    inputIsActive () {
      const activeElement = document.activeElement
      return [this.lengthGroupAutocompleteId,
        this.diameterGroupAutocompleteId,
        'species-group-label',
        'species-selector'].includes(activeElement.id)
    },

    sortSpecies () {
      this.speciesGroupBuilder.species.sort(speciesSorter)
    },

    onSpeciesChanged (s) {
      this.speciesGroupBuilder.species = s
      this.sortSpecies()
    },

    onLengthGroupsChanged (lg) {
      this.speciesGroupBuilder.lengthGroups = lg
      this.speciesGroupBuilder.lengthGroups.sort(lengthGroupSorter)
    },

    onDiameterGroupsChanged (dg) {
      this.speciesGroupBuilder.diameterGroups = dg
      this.speciesGroupBuilder.diameterGroups.sort(diameterGroupSorter)
    },

    isBeingEdited (speciesGroup) {
      return speciesGroup?.speciesGroupId === this.speciesGroupBuilder?.speciesGroupId
    }
  }
}
</script>
